import { useEffect, useState } from "react";
import { useConferences } from "@/hooks/useSiteContent";

/**
 * Slim horizontally-scrolling banner listing conferences we track/attend.
 * - Deep forest green background with cream text — inverse of the page palette
 *   so it reads as a distinct strip sitting just under the sticky header.
 * - Continuous seamless marquee, paused on hover.
 * - Respects prefers-reduced-motion: renders a static, horizontally-scrollable
 *   row with NO animation.
 * - Slot in the layout: fixed strip below <Header>, above main content.
 */
const SEPARATOR = "✦";

function detectReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

const Entry = ({ c }) => {
  const label = [c.name, c.dates, c.location].filter(Boolean).join(" — ");
  if (c.url) {
    return (
      <a
        href={c.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`conference-entry-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
        className="text-brand-cream hover:text-brand-accent transition-colors whitespace-nowrap"
      >
        {label}
      </a>
    );
  }
  return (
    <span
      data-testid={`conference-entry-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
      className="text-brand-cream/85 whitespace-nowrap"
    >
      {label}
    </span>
  );
};

export default function ConferenceBanner() {
  const { data } = useConferences();
  const conferences = data?.conferences || [];
  const [reduced, setReduced] = useState(false);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    setReduced(detectReducedMotion());
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e) => setReduced(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  if (!conferences.length) return null;

  const entriesRow = (
    <div className="flex items-center gap-8">
      {conferences.map((c, i) => (
        <div key={c.name + i} className="flex items-center gap-8">
          <Entry c={c} />
          <span className="text-brand-accent/70 select-none" aria-hidden="true">
            {SEPARATOR}
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <div
      data-testid="conference-banner"
      className="relative border-b border-brand-deep/60 bg-brand-deep text-brand-cream"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mx-auto flex items-center h-9 overflow-hidden">
        <span
          aria-hidden="true"
          className="hidden sm:inline-flex items-center gap-2 pl-4 pr-5 mr-2 text-[10px] font-semibold tracking-[0.28em] uppercase text-brand-accent/90 flex-shrink-0 border-r border-brand-cream/15"
        >
          Now Tracking
        </span>

        {reduced ? (
          // Static, horizontally scrollable row — no animation
          <div
            data-testid="conference-banner-static"
            className="flex-1 overflow-x-auto whitespace-nowrap py-1 pl-3 pr-6 text-xs font-medium tracking-wide"
          >
            {entriesRow}
          </div>
        ) : (
          <div
            data-testid="conference-banner-marquee"
            className="flex-1 overflow-hidden whitespace-nowrap"
          >
            <div
              className="flex w-max items-center gap-8 will-change-transform"
              style={{
                animation: "conf-marquee 40s linear infinite",
                animationPlayState: paused ? "paused" : "running",
              }}
            >
              {/* Duplicated content for a seamless loop */}
              <div className="flex items-center gap-8 pr-8 text-xs font-medium tracking-wide">
                {entriesRow}
              </div>
              <div
                aria-hidden="true"
                className="flex items-center gap-8 pr-8 text-xs font-medium tracking-wide"
              >
                {entriesRow}
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes conf-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}
